import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EntityType } from '../types';
import { limsClient } from '../api/limsClient';
import { VIEW_REGISTRY } from '../config/viewsConfig';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { useEditMode } from '../context/EditModeContext';
import { LabSelector } from './LabSelector';
import { useLab, entityHasLabId } from '../LabContext';
import {
  SampleIcon,
  TestIcon,
  AssayIcon,
  OrderIcon,
  ViewsIcon,
  EventsIcon,
  DashboardIcon,
  SunIcon,
  MoonIcon,
  LogOutIcon,
  ChevronDownIcon,
  ParserLogIcon,
  SettingsIcon,
} from './icons';

function UserInitial() {
  const user = useAuth();
  const initial = (user?.email || '?').charAt(0).toUpperCase();
  return <>{initial}</>;
}

function UserEmailRow() {
  const user = useAuth();
  if (!user?.email) return null;
  return (
    <>
      <div className="user-dropdown-email">{user.email}</div>
      <div className="user-dropdown-divider" />
    </>
  );
}

function UserSignOutRow() {
  const handleSignOut = async () => {
    await fetch('/auth/logout', { method: 'POST' });
    window.location.href = '/';
  };
  return (
    <>
      <div className="user-dropdown-divider" />
      <button className="user-dropdown-item" onClick={handleSignOut}>
        <LogOutIcon /> Sign out
      </button>
    </>
  );
}

function UserMenu() {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = useAuth();
  const navigate = useNavigate();
  const { isEditMode, toggleEditMode } = useEditMode();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="user-menu" ref={ref}>
      <button className="user-avatar" onClick={() => setOpen(!open)}>
        {user?.email ? <UserInitial /> : <SettingsIcon />}
      </button>
      {open && (
        <div className="user-dropdown">
          {user?.email && <UserEmailRow />}
          <button className="user-dropdown-item" onClick={toggleTheme}>
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          {user?.isAdmin && (
            <>
              <button
                className={`user-dropdown-item${isEditMode ? ' user-dropdown-item-active' : ''}`}
                onClick={toggleEditMode}
              >
                <SettingsIcon />
                {isEditMode ? 'Exit Edit Mode' : 'Edit Mode'}
              </button>
              <button className="user-dropdown-item" onClick={() => { navigate('/admin/users'); setOpen(false); }}>
                <SettingsIcon /> Admin
              </button>
            </>
          )}
          {user?.email && <UserSignOutRow />}
        </div>
      )}
    </div>
  );
}

type QuickFindResult = { entityType: EntityType; id: number; found: boolean };

export function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [quickFindId, setQuickFindId] = useState('');
  const [quickFindResults, setQuickFindResults] = useState<QuickFindResult[]>([]);
  const [quickFindLoading, setQuickFindLoading] = useState(false);
  const [quickFindOpen, setQuickFindOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const quickFindInputRef = useRef<HTMLInputElement | null>(null);
  const quickFindRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  const { labs, labFilters } = useLab();
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const entries: { key: string; entityType: string; filters: any[] }[] = [];

    for (const view of Object.values(VIEW_REGISTRY)) {
      if (view.filters?.length) {
        const merged = entityHasLabId(view.entityType) && labFilters.length > 0
          ? [...view.filters, ...labFilters]
          : view.filters;
        entries.push({ key: `reg:${view.id}`, entityType: view.entityType, filters: merged });
      }
    }

    if (entries.length === 0) return;

    let cancelled = false;
    Promise.all(
      entries.map(e =>
        limsClient.getEntityCount(e.entityType, e.filters)
          .then(count => ({ key: e.key, count }))
          .catch(() => ({ key: e.key, count: -1 }))
      )
    ).then(results => {
      if (cancelled) return;
      const counts: Record<string, number> = {};
      for (const r of results) counts[r.key] = r.count;
      setViewCounts(counts);
    });

    return () => { cancelled = true; };
  }, [labFilters]);

  const path = location.pathname;
  const pathParts = path.split('/').filter(p => p);

  const isWorkflowActive =
    path === '/' || path === '/dashboard' ||
    path.startsWith('/sample') || path.startsWith('/test') ||
    path.startsWith('/assay') || path.startsWith('/order');
  const isViewsActive = path.startsWith('/views/');
  const isActivityActive = path === '/events' || path === '/parser-logs';

  const activeViewId = isViewsActive ? pathParts[1] : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenu && navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
      if (quickFindOpen && quickFindRef.current && !quickFindRef.current.contains(event.target as Node)) {
        setQuickFindOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenu, quickFindOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        quickFindInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleMenuToggle = (menu: string) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const handleDropdownNav = (to: string) => {
    navigate(to);
    setOpenMenu(null);
    setMobileMenuOpen(false);
  };

  const handleQuickFindKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const trimmed = quickFindId.trim();
    if (!trimmed) return;

    const id = parseInt(trimmed, 10);
    if (isNaN(id) || String(id) !== trimmed) return;

    setQuickFindLoading(true);
    setQuickFindOpen(true);

    const types: EntityType[] = ['sample', 'test', 'assay', 'order'];
    const results = await Promise.allSettled([
      limsClient.getSamples(id),
      limsClient.getTests(id),
      limsClient.getAssays(id),
      limsClient.getOrders(id),
    ]);
    const found = results
      .map((r, i) => ({ entityType: types[i], id, found: r.status === 'fulfilled' }))
      .filter((r) => r.found);
    setQuickFindResults(found);

    setQuickFindLoading(false);
  };

  const handleQuickFindResultClick = (entityType: EntityType, id: number) => {
    navigate(`/${entityType}/${id}`);
    setQuickFindOpen(false);
    setQuickFindId('');
    setQuickFindResults([]);
  };

  const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
  const hasRegistryViews = Object.keys(VIEW_REGISTRY).length > 0;

  return (
    <nav className="app-topnav" ref={navRef}>
      <div className="topnav-brand">
        <div className="topnav-brand-link" onClick={() => navigate('/dashboard')}>
          <span className="topnav-brand-icon" role="img" aria-label="Mariana" style={{ WebkitMaskImage: 'url(/trident.png)', maskImage: 'url(/trident.png)' }} />
          <span className="topnav-brand-text">Mariana Labs</span>
        </div>
        {labs.length > 1 && <span className="topnav-brand-separator">|</span>}
        <LabSelector />
      </div>

      <button className="topnav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {mobileMenuOpen
            ? <path d="M18 6L6 18M6 6l12 12" />
            : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
          }
        </svg>
      </button>

      <div className={`topnav-menus${mobileMenuOpen ? ' mobile-open' : ''}`}>
        <div className="topnav-menu">
          <button
            className={`topnav-menu-trigger${openMenu === 'workflow' || isWorkflowActive ? ' active' : ''}`}
            onClick={() => handleMenuToggle('workflow')}
          >
            Entities <ChevronDownIcon />
          </button>
          {openMenu === 'workflow' && (
            <div className="topnav-dropdown">
              <button
                className={`topnav-dropdown-item${path === '/' || path === '/dashboard' ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/dashboard')}
              >
                <DashboardIcon /> Dashboard
              </button>
              <div className="topnav-dropdown-divider" />
              <button
                className={`topnav-dropdown-item${path.startsWith('/sample') ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/sample')}
              >
                <SampleIcon /> Samples
              </button>
              <button
                className={`topnav-dropdown-item${path.startsWith('/test') ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/test')}
              >
                <TestIcon /> Tests
              </button>
              <button
                className={`topnav-dropdown-item${path.startsWith('/assay') ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/assay')}
              >
                <AssayIcon /> Assays
              </button>
              <button
                className={`topnav-dropdown-item${path.startsWith('/order') ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/order')}
              >
                <OrderIcon /> Orders
              </button>
            </div>
          )}
        </div>

        {hasRegistryViews && (
          <div className="topnav-menu">
            <button
              className={`topnav-menu-trigger${openMenu === 'views' || isViewsActive ? ' active' : ''}`}
              onClick={() => handleMenuToggle('views')}
            >
              Views <ChevronDownIcon />
            </button>
            {openMenu === 'views' && (
              <div className="topnav-dropdown topnav-dropdown-views">
                {Object.values(VIEW_REGISTRY).map(view => (
                  <button
                    key={view.id}
                    className={`topnav-dropdown-item${activeViewId === view.id ? ' active' : ''}`}
                    onClick={() => handleDropdownNav(`/views/${view.id}`)}
                  >
                    <ViewsIcon /> {view.label}
                    {viewCounts[`reg:${view.id}`] != null && viewCounts[`reg:${view.id}`] >= 0 && (
                      <span className="topnav-view-count">{viewCounts[`reg:${view.id}`].toLocaleString()}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="topnav-menu">
          <button
            className={`topnav-menu-trigger${openMenu === 'activity' || isActivityActive ? ' active' : ''}`}
            onClick={() => handleMenuToggle('activity')}
          >
            Activity <ChevronDownIcon />
          </button>
          {openMenu === 'activity' && (
            <div className="topnav-dropdown">
              <button
                className={`topnav-dropdown-item${path === '/events' ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/events')}
              >
                <EventsIcon /> Events
              </button>
              <button
                className={`topnav-dropdown-item${path === '/parser-logs' ? ' active' : ''}`}
                onClick={() => handleDropdownNav('/parser-logs')}
              >
                <ParserLogIcon /> Parser Logs
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="topnav-right">
        <div className="topnav-quick-find" ref={quickFindRef}>
          <input
            ref={quickFindInputRef}
            type="text"
            className="quick-find-input"
            placeholder="Find by ID..."
            aria-label="Quick find entity by ID"
            value={quickFindId}
            onChange={(e) => setQuickFindId(e.target.value)}
            onKeyDown={handleQuickFindKeyDown}
          />
          <span className="quick-find-shortcut">{isMac ? 'Cmd+K' : 'Ctrl+K'}</span>
          {quickFindOpen && (
            <div className="quick-find-results">
              {quickFindLoading ? (
                <div className="quick-find-loading">Searching...</div>
              ) : quickFindResults.length === 0 ? (
                <div className="quick-find-empty">
                  No entities found with ID {quickFindId}
                </div>
              ) : (
                quickFindResults.map((r) => (
                  <button
                    key={`${r.entityType}-${r.id}`}
                    className="quick-find-result"
                    onClick={() => handleQuickFindResultClick(r.entityType, r.id)}
                  >
                    <span className="qf-type">{r.entityType.charAt(0).toUpperCase() + r.entityType.slice(1)}</span>
                    <span className="qf-id">#{r.id}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <UserMenu />
      </div>
    </nav>
  );
}
